import React                                              from "react"
import { useGlobalConfig }                                from "context/GlobalContext"
import {
  Settings as LucideSettings,
  Palette as LucidePalette,
  Info as LucideInfo,
  ChevronDown as LucideChevronDown,
  HelpCircle as LucideHelpCircle,
  Users as LucideUsers,
  ClipboardList as LucideClipboardList,
  SlidersHorizontal as LucideSliders,
  Server as LucideServer,
} from 'lucide-react'
import {  
  Typography, 
  List, 
  ListItem, 
  Box, 
  ListItemButton, 
  ListItemIcon, 
  ListItemText, 
  Collapse,
  Select,
  FormControl,
  MenuItem,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails
}                                                         from "@mui/material"
import { useSelector, useDispatch } from "react-redux";
import { selectPilotList, selectSelectedPilotId,
         selectAssociatedEnvironmentsRevision,
         updatePilotList
       }                                                  from "../../store/dataReducers/pilotsSlice";
import { setProjectAttributes, selectProjectAttributes } from "../../store/dataReducers/applicationScopeSlice";
import type { ProjectAttributes } from "types/apis/applicationScopeInterfaces";
import { ROUTE_TREE }                                     from "routes/AppRoutes";
import { useCanAccessRoute }                              from "hooks/usePermissions";
import { useState, useEffect, useCallback, useRef } from "react";
import { PilotApis } from "apis/pilotAPI";
import { APIData } from "types/cms/frontendTypes";
import { useToast } from '../../context/ToastContext';
import { useLocation } from 'react-router-dom';
import ExpandMoreIcon                                     from '@mui/icons-material/ExpandMore';

// Extended utility interface with environment_id
interface ExtendedUtility extends APIData<"api::utility.utility"> {
}

export type PilotDrawerProps = {
  isExpanded : boolean,
}

export interface ProjectEnvironment {
  id: string;
  name: string;
  [key: string]: unknown;
}

export const PilotDrawer = ({
    isExpanded,
  } : PilotDrawerProps) => {

  const { rc } = useGlobalConfig()
  const location = useLocation();
  const toast = useToast();
  const canAccessRoute = useCanAccessRoute();

  const utilityList = useSelector(selectPilotList) as any[]
  const selectedUtilityId = useSelector(selectSelectedPilotId)
  const associatedEnvironmentsRevision = useSelector(selectAssociatedEnvironmentsRevision)
  const projectAttributesFromRedux = useSelector(selectProjectAttributes)
  const selectedUtility = utilityList.find((util) => util.projectId === selectedUtilityId) as any
  const dispatch = useDispatch();
  
  // Check if project type is MIGRATED (case-insensitive)
  const isMigratedProject = selectedUtility?.tag?.toLowerCase().includes('migrated');

  // State for environments and loading
  const [environments, setEnvironments] = useState<ProjectEnvironment[]>([]);
  const [isLoadingEnvironments, setIsLoadingEnvironments] = useState(false);
  const [selectedEnvironment, setSelectedEnvironment] = useState<string>('');

  // Accordion expanded state per group
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});

  // Use refs to avoid dependency issues
  const rcRef = useRef(rc);
  const toastRef = useRef(toast);
  const lastFetchedProjectIdRef = useRef<string | null>(null);
  
  // Update refs when values change
  useEffect(() => {
    rcRef.current = rc;
    toastRef.current = toast;
  }, [rc, toast]);

  // Function to fetch associated environments for a project
  const fetchAssociatedEnvironments = useCallback(async (projectId: string) => {
    console.log('fetchAssociatedEnvironments called for projectId:', projectId);
    setIsLoadingEnvironments(true);
    setEnvironments([]);
    
    try {
      const response = await PilotApis.getAssociatedEnvironments(rcRef.current, projectId);
      
      if (response.error) {
        const errorMessage = response.error.message || 'Failed to fetch associated environments';
        console.error('Error fetching associated environments:', errorMessage);
        toastRef.current.error(errorMessage);
        setEnvironments([]);
      } else {
        const environmentsData = response.data?.data || [];
        setEnvironments(environmentsData);
        
        if (environmentsData.length === 0) {
          toastRef.current.info('No environments associated with this project');
        }
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      console.error('Exception fetching associated environments:', errorMessage);
      toastRef.current.error(errorMessage);
      setEnvironments([]);
    } finally {
      setIsLoadingEnvironments(false);
    }
  }, []); // Empty dependency array since we're using refs

  // Fetch project attributes and save to Redux
  const fetchProjectAttributes = useCallback(async (projectId: string) => {
    if (!projectId) return;

    try {
      const response = await PilotApis.getProjectAttributes(rcRef.current, projectId);
      
      if (response.success && response.data) {
        // Extract project attributes from the response
        const { 
          allowed_measurement_type, 
          supported_meter_types, 
          default_user_segment,
          country,
          default_timezone,
          primary_language,
          supported_locale
        } = response.data?.attributes;
        
        // Save project attributes to Redux
        const projectAttributes: ProjectAttributes = {
          customer_type: default_user_segment || null,
          fuel_type: allowed_measurement_type || null,
          meter_type: supported_meter_types 
            ? (typeof supported_meter_types === 'string' 
                ? (() => {
                    try {
                      const parsed = JSON.parse(supported_meter_types);
                      return Array.isArray(parsed) ? parsed.join(', ') : String(parsed);
                    } catch {
                      return supported_meter_types;
                    }
                  })()
                : Array.isArray(supported_meter_types) 
                  ? supported_meter_types.join(', ')
                  : String(supported_meter_types))
            : null,
          country: country || null,
          timezone: default_timezone || null,
          language: primary_language || null,
          supported_locale: supported_locale ?? null,
        };
        
        dispatch(setProjectAttributes(projectAttributes));
        lastFetchedProjectIdRef.current = projectId;
        console.log('Project attributes fetched and saved to Redux from PilotDrawer:', projectAttributes);
      } else {
        console.error('Failed to fetch project attributes in PilotDrawer:', response.error);
      }
    } catch (err) {
      console.error('Error fetching project attributes in PilotDrawer:', err);
    }
  }, [dispatch]);

  // Fetch associated environments when selectedUtility changes
  useEffect(() => {
    if (selectedUtility?.projectId) {
      const projectId = selectedUtility.projectId;
      // Only fetch if we haven't fetched for this project yet
      // Check projectAttributesFromRedux inside the effect but don't depend on it to avoid infinite loop
      const shouldFetch = lastFetchedProjectIdRef.current !== projectId;
      
      if (shouldFetch) {
        // Set ref immediately to prevent duplicate calls (e.g., React strict mode double render)
        lastFetchedProjectIdRef.current = projectId;
        fetchProjectAttributes(projectId);
      }
      fetchAssociatedEnvironments(projectId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUtility?.projectId]);

  // Debug logging
  useEffect(() => {
    console.log('PilotDrawer state:', {
      selectedUtility: selectedUtility?.projectId,
      environments: environments.length,
      selectedEnvironment,
      isLoadingEnvironments
    });
  }, [selectedUtility, environments, selectedEnvironment, isLoadingEnvironments]);

  // Set default environment when environments are loaded
  useEffect(() => {
    if (environments.length > 0) {
      // Use the environment ID from the project object, but validate it exists in current environments
      if (selectedUtility?.environment_id) {
        const envId = String(selectedUtility.environment_id);
        const envExists = environments.some(env => String(env.id) === envId);
        if (envExists) {
          setSelectedEnvironment(envId);
        } else {
          console.warn(`Environment ID ${selectedUtility.environment_id} not found in current environments list`);
          setSelectedEnvironment('');
        }
      } else {
        setSelectedEnvironment('');
      }
    } else {
      setSelectedEnvironment('');
    }
  }, [environments, selectedUtility]);

  // Handler for environment change
  const handleEnvironmentChange = (event: any) => {
    console.log('handleEnvironmentChange called with event:', event);
    const newEnvironmentId = event.target?.value || event.value;
    console.log('newEnvironmentId:', newEnvironmentId);
    
    // Update local state immediately
    setSelectedEnvironment(newEnvironmentId);
    
    // Update the project's selected environment ID in the database
    if (selectedUtility && selectedUtility.projectId && newEnvironmentId) {
      // Use async function for API call
      const updateEnvironment = async () => {
        try {
          const response = await PilotApis.updateProjectEnvironment(rc, selectedUtility.projectId, parseInt(newEnvironmentId));
        
          if (response.error) {
            const errorMessage = response.error.message || 'Failed to update environment';
            console.error('Error updating project environment:', errorMessage);
            toast.error(errorMessage);
            
            // Revert the selection on error
            setSelectedEnvironment(selectedUtility.environment_id || '');
          } else {
            console.log('Project environment updated successfully');
            
            // Get the environment name for the toast message
            const selectedEnv = environments.find(env => env.id === newEnvironmentId);
            const envName = (selectedEnv as any)?.environment_name || (selectedEnv as any)?.name || 'Environment';
            
            toast.success(`Primary environment updated to ${envName}`);
            
            // Update the Redux store with the updated project data
            if (response.data) {
              const updatedPilotList = utilityList.map(pilot => {
                if (pilot.projectId === selectedUtility.projectId) {
                  return {
                    ...pilot,
                    environment_id: newEnvironmentId,
                    environment: environments.find(env => env.id === newEnvironmentId)
                  } as ExtendedUtility;
                }
                return pilot;
              });
              
              dispatch(updatePilotList(updatedPilotList));
            }
          }
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
          console.error('Exception updating project environment:', errorMessage);
          toast.error(errorMessage);
          
          // Revert the selection on error
          setSelectedEnvironment(selectedUtility.environment_id || '');
        }
      };
      
      // Call the async function
      updateEnvironment();
    }
  };

  const isActive = (pathPrefix: string) => location.pathname.startsWith(pathPrefix);

  const navToCMSStats = () => {
    rc.uiRouter.navigate(ROUTE_TREE.CMS.CONTENT)
  }

  const navToCMSRecommendations = () => {
    rc.uiRouter.navigate(ROUTE_TREE.CMS.RECO.LIST)
  }

  const navToSurvey = () => {
    rc.uiRouter.navigate(ROUTE_TREE.CMS.SURVEY.HOME)
  }

  const navToCMSHomeProfile = () => {
    rc.uiRouter.navigate(ROUTE_TREE.CMS.HOME_PROFILE)
  }

  const navToCMSAppProfile = () => {
    rc.uiRouter.navigate(ROUTE_TREE.CMS.APPLIANCE_PROFILE)
  }

  const navToCXEditor = () => {
    rc.uiRouter.navigate(ROUTE_TREE.CONTENT_MANAGEMENT.WYSIWYG)
  }

  const navToColorPalette = () => {
    rc.uiRouter.navigate(ROUTE_TREE.CONTENT_MANAGEMENT.COLOR_PALETTE)
  }

  const navToDataScenarios = () => {
    rc.uiRouter.navigate(ROUTE_TREE.CONTENT_MANAGEMENT.DATA_SCENARIOS)
  }

  const navToMediaContent = () => {
    rc.uiRouter.navigate(ROUTE_TREE.CONTENT_MANAGEMENT.MEDIA_CONTENT)
  }

  const navToDetails = () => {
    rc.uiRouter.navigate(ROUTE_TREE.PROJECT.DETAILS)
  }

  const navToProjectConfigs = () => {
    rc.uiRouter.navigate(ROUTE_TREE.PROJECT.CONFIGS)
  }

  const navToProjectAssistant = () => {
    rc.uiRouter.navigate(ROUTE_TREE.PROJECT.ASSISTANT)
  }

  const navToCustomerDataGenerator = () => {
    rc.uiRouter.navigate(ROUTE_TREE.PROJECT.CUSTOMER_DATA_GENERATOR)
  }

  const navToAuditTrail = () => {
    rc.uiRouter.navigate(ROUTE_TREE.PROJECT.AUDIT_TRAIL)
  }

  const navToDCXClusters = () => {
    rc.uiRouter.navigate(ROUTE_TREE.CONFIG_TOOLS.DCX_CLUSTERS)
  }

  const navToConfigCompare = () => {
    rc.uiRouter.navigate(ROUTE_TREE.CONFIG_TOOLS.CONFIG_COMPARE)
  }

  // Toggle a group in accordion
  const toggleGroup = (key: string) => {
    setExpandedGroups(prev => {
      const isOpen = !!prev[key];
      if (isOpen) {
        return {};
      }
      return { [key]: true };
    });
  };

  const getCMSEntries = () => {
    return (
      <>  
        <ListItemButton onClick={navToCMSStats} selected={isActive('/pilot/content')} dense sx={{ py: 0.75, color: 'inherit', opacity: 1 }}>
          <ListItemText primary="Statistics" primaryTypographyProps={{ sx: { color: 'inherit', opacity: 1 } }} />
        </ListItemButton>
        <ListItemButton onClick={navToCMSHomeProfile} selected={isActive('/attributes/home-profile')} dense sx={{ py: 0.75, color: 'inherit', opacity: 1 }}>
          <ListItemText primary="Home Profile" primaryTypographyProps={{ sx: { color: 'inherit', opacity: 1 } }} />
        </ListItemButton>
        <ListItemButton onClick={navToCMSAppProfile} selected={isActive('/attributes/appliance-profile')} dense sx={{ py: 0.75, color: 'inherit', opacity: 1 }}>
          <ListItemText primary="Appliance Profile" primaryTypographyProps={{ sx: { color: 'inherit', opacity: 1 } }} />
        </ListItemButton>
        <ListItemButton onClick={navToCMSRecommendations} selected={isActive('/reco/list')} dense sx={{ py: 0.75, color: 'inherit', opacity: 1 }}>
          <ListItemText primary="Recommendations" primaryTypographyProps={{ sx: { color: 'inherit', opacity: 1 } }} />
        </ListItemButton>
        <ListItemButton onClick={navToSurvey} selected={isActive('/cms/survey')} dense sx={{ py: 0.75, color: 'inherit', opacity: 1 }}>
          <ListItemText primary="Survey" primaryTypographyProps={{ sx: { color: 'inherit', opacity: 1 } }} />
        </ListItemButton>
      </>
    )
  }

  // Initialize open group when route matches
  useEffect(() => {
    const newState: Record<string, boolean> = { ...expandedGroups };
    if (location.pathname.startsWith('/workflow')) newState['featureWorkflows'] = true;
    if (location.pathname.startsWith('/content') || location.pathname.startsWith('/content-management')) {
      newState['contentManagement'] = true;
    }
    if (location.pathname.startsWith('/deployment-tools')) newState['deploymentTools'] = true;
    if (location.pathname.startsWith('/config') || location.pathname.startsWith('/project/configs')) newState['configurationTools'] = true;
    if (location.pathname.startsWith('/data-analysis')) newState['dataAnalysisTools'] = true;
    setExpandedGroups(newState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const headerTextSx = { color: 'inherit', fontWeight: 600, fontSize: 14 } as const;
  const headerIconSx = { fontSize: 18, opacity: 0.9 } as const;

  return (
    selectedUtility && (
    <React.Fragment>
      {/* Utility Name/Image and Project/Environment Header */}
      {isExpanded && (
      <ListItem disablePadding>
        <Box sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          // margin: 1,xw
          padding: .5,
          backgroundColor: 'transparent',
          borderRadius: 0,
          overflow: 'hidden',
          alignItems: isExpanded ? 'flex-start' : 'center',
        }}>
          <Accordion disableGutters elevation={0} square defaultExpanded sx={{ bgcolor: 'transparent', width: '100%' }}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon sx={{ color: 'inherit', opacity: 0.8, fontSize: 18 }} />}
              sx={{ minHeight: 0, px: 0, '& .MuiAccordionSummary-content': { my: 0, py: 0.25 } }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, width: '100%' }}>
                <Typography variant="caption" sx={{ color: "#BFCCD9" }} noWrap>
                  Project ID: {selectedUtility?.projectId}
                </Typography>
                <Typography variant="body2" sx={{ fontSize: 16, lineHeight: 1.2,mb:.5, color: "#BFCCD9" }} noWrap title={selectedUtility?.name}>
                  {selectedUtility?.name}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0, px: 0 }}>
              <Box sx={{ width: '100%', mt: 0.5, bgcolor: "#001a33", p: 1.25, borderRadius: 1.25, boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.08)', border: '1px solid', borderColor: 'rgba(255,255,255,0.12)' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, letterSpacing: 0.2, fontSize: 13, display: 'flex', alignItems: 'center', gap: 0.5, color: "#BFCCD9" }}>
                    <LucideServer size={16} style={{ marginRight: 6 }} /> 
                    Environments
                  </Typography>
                  {isLoadingEnvironments && (
                    <CircularProgress size={12} sx={{ color: 'rgba(255,255,255,0.6)' }} />
                  )}
                </Box>
                <FormControl fullWidth size="small" variant="outlined">
                  <Select
                    id="environment-select-sidebar"
                    value={selectedEnvironment || ''}
                    onChange={handleEnvironmentChange}
                    disabled
                    displayEmpty
                    renderValue={(value: any) => {
                      if (!value) return <span style={{ opacity: 0.6 }}>{isLoadingEnvironments ? 'Loading environments...' : 'Select Associated Environment…'}</span> as any;
                      const env = environments.find(e => String(e.id) === String(value)) as any;
                      if (!env) return String(value);
                      const projectName = (selectedUtility?.name ?? '').trim();
                      const baseName = String(env?.environment_name ?? value ?? '').trim();
                      const envPart = baseName ? baseName.toUpperCase() : '';
                      let core = projectName ? `${envPart}_${projectName}` : envPart || String(value);
                      const type = String(env?.type ?? '').trim().toLowerCase();
                      const stackName = String(env?.stack_name ?? '').trim();
                      if (type === 'dev') core = `DEV_${core}`;
                      else if (type === 'uat') core = `UAT_${core}`;
                      return (
                        <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {core}
                          {stackName && (type === 'dev' || type === 'uat') && (
                            <span style={{ fontSize: 11, opacity: 0.9 }}> ({stackName})</span>
                          )}
                        </span>
                      ) as any;
                    }}
                    sx={{
                      width: '100%',
                      borderRadius: 1.5,
                      color: "#BFCCD9",
                      bgcolor: '#0B2641B2',
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(148,163,184,0.18)' },
                      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(148,163,184,0.28)' },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main', boxShadow: '0 0 0 2px rgba(16,185,129,0.25)' },
                      '& .MuiSelect-select': { textAlign: 'left', fontFamily: 'inherit', fontSize: 13, py: 1, px: 1.5, minHeight: 0, display: 'flex', alignItems: 'center' },
                      '& svg': { color: 'inherit', opacity: 0.8 },
                      '&.Mui-disabled': {
                        color: "#BFCCD9 !important",
                        opacity: 1,
                        '& .MuiOutlinedInput-input': {
                          color: "#BFCCD9 !important",
                          WebkitTextFillColor: "#BFCCD9 !important",
                        },
                        '& .MuiSelect-select': { 
                          color: "#BFCCD9 !important",
                          WebkitTextFillColor: "#BFCCD9 !important",
                        },
                        '& svg': { color: "#BFCCD9 !important", opacity: 0.8 },
                      },
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          bgcolor: 'rgba(2,6,23,0.98)',
                          color: 'rgba(226,232,240,0.92)',
                          border: '1px solid',
                          borderColor: 'rgba(148,163,184,0.18)',
                          '& .MuiMenuItem-root': { fontSize: 13 },
                          '& .MuiMenuItem-root:hover': { bgcolor: 'rgba(148,163,184,0.10)' },
                          '& .MuiMenuItem-root.Mui-selected': { bgcolor: 'rgba(0,200,180,0.22)' },
                          '& .MuiMenuItem-root.Mui-selected:hover': { bgcolor: 'rgba(0,200,180,0.28)' },
                        }
                      }
                    }}
                  >
                    {environments.length === 0 ? (
                      <MenuItem value="" disabled>
                        {isLoadingEnvironments ? 'Loading environments...' : 'No associated environments'}
                      </MenuItem>
                    ) : [
                      <MenuItem key="placeholder" value="" disabled>
                        Select Associated Environment…
                      </MenuItem>,
                      ...environments.map((env) => {
                        const projectName = (selectedUtility?.name ?? '').trim();
                        const baseName = String((env as any)?.environment_name ?? env.id ?? '').trim();
                        const envPart = baseName ? baseName.toUpperCase() : '';
                        let core = projectName ? `${envPart}_${projectName}` : envPart || String(env.id);
                        const type = String((env as any)?.type ?? '').trim().toLowerCase();
                        const stackName = String((env as any)?.stack_name ?? '').trim();
                        if (type === 'dev') core = `DEV_${core}`;
                        else if (type === 'uat') core = `UAT_${core}`;
                        return (
                          <MenuItem key={String(env.id)} value={env.id as any} sx={{ fontSize: 13 }}>
                            {core}
                            {stackName && (type === 'dev' || type === 'uat') && (
                              <span style={{ fontSize: 11, opacity: 0.9 }}> ({stackName})</span>
                            )}
                          </MenuItem>
                        );
                      })
                    ]}
                  </Select>
                </FormControl>
              </Box>
            </AccordionDetails>
          </Accordion>
        </Box>
      </ListItem> 
      )}  

      {/* Top-level items */}
      {canAccessRoute(ROUTE_TREE.PROJECT.DETAILS) && (
        <ListItem disablePadding>
          <ListItemButton
            onClick={navToDetails}
            selected={isActive('/project/details')}
            sx={!isExpanded ? {
              display: 'flex',
              justifyContent: 'center',
              padding: '8px !important'
            } : {}}
          >
            <ListItemIcon sx={{
              minWidth : isExpanded ? '30px' : 'auto',
            }}>
              <LucideInfo size={18} />
            </ListItemIcon>
            {isExpanded && <ListItemText primary="Project Details" primaryTypographyProps={{ sx: { fontWeight: 500, fontSize: 14, color: 'inherit' } }} />}
          </ListItemButton>
        </ListItem>
      )}

      {/* Project Assistant - Hide for migrated projects */}
      {!isMigratedProject && (
        <ListItem disablePadding>
          <ListItemButton 
            onClick={navToProjectAssistant} 
            selected={isActive('/pilot/assistant')}
            sx={!isExpanded ? {
              display: 'flex',
              justifyContent: 'center',
              padding: '8px !important'
            } : {}}
          >
            <ListItemIcon sx={{ minWidth: isExpanded ? '30px' : 'auto' }}>
              <LucideHelpCircle size={18} />
            </ListItemIcon>
            {isExpanded && <ListItemText primary="Project Assistant" primaryTypographyProps={{ sx: { fontWeight: 500, fontSize: 14, color: 'inherit' } }} />}
          </ListItemButton>
        </ListItem>
      )}
      {/* Project Audit - Hide for migrated projects */}
      {!isMigratedProject && canAccessRoute(ROUTE_TREE.PROJECT.AUDIT_TRAIL) && (
        <ListItem disablePadding>
          <ListItemButton
            onClick={navToAuditTrail}
            selected={isActive('/project/audit-trail')}
            sx={!isExpanded ? {
              display: 'flex',
              justifyContent: 'center',
              padding: '8px !important'
            } : {}}
          >
            <ListItemIcon sx={{ minWidth: isExpanded ? '30px' : 'auto' }}>
              <LucideClipboardList size={18} />
            </ListItemIcon>
            {isExpanded && <ListItemText primary="Project Audit" primaryTypographyProps={{ sx: { fontWeight: 500, fontSize: 14, color: 'inherit' } }} />}
          </ListItemButton>
        </ListItem>
      )}

      {/* Customer Data Generator - Hide for migrated projects */}
      {!isMigratedProject && canAccessRoute(ROUTE_TREE.PROJECT.CUSTOMER_DATA_GENERATOR) && (
        <ListItem disablePadding>
          <ListItemButton 
            onClick={navToCustomerDataGenerator} 
            selected={isActive('/pilot/customer-data')}
            sx={!isExpanded ? {
              display: 'flex',
              justifyContent: 'center',
              padding: '8px !important'
            } : {}}
          >
            <ListItemIcon sx={{ minWidth: isExpanded ? '30px' : 'auto' }}>
              <LucideUsers size={18} />
            </ListItemIcon>
            {isExpanded && <ListItemText primary="Customer Data Generator" primaryTypographyProps={{ sx: { fontWeight: 500, fontSize: 14, color: 'inherit' } }} />}
          </ListItemButton>
        </ListItem>
      )}

      {/* Feature Workflows - Hide for migrated projects */}
      {!isMigratedProject && (
        <>
          <ListItem disablePadding>
            <ListItemButton 
              onClick={() => toggleGroup('featureWorkflows')} 
              selected={expandedGroups['featureWorkflows'] || isActive('/workflow')} 
              sx={!isExpanded ? {
                display: 'flex',
                justifyContent: 'center',
                padding: '8px !important'
              } : { gap: 1 }}
            >
              <ListItemIcon sx={{ minWidth: isExpanded ? '30px' : 'auto' }}>
                <LucideSliders size={18} style={{ fontSize: headerIconSx.fontSize, opacity: headerIconSx.opacity }} />
              </ListItemIcon>
              {isExpanded && <ListItemText primary="Feature Workflows" primaryTypographyProps={{ sx: headerTextSx }} />}
              {isExpanded && (
                <LucideChevronDown size={18} style={{ marginLeft: 'auto', transform: expandedGroups['featureWorkflows'] ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }} />
              )}
            </ListItemButton>
          </ListItem>
          <Collapse in={expandedGroups['featureWorkflows'] && isExpanded} timeout="auto" unmountOnExit>
            <Box sx={{ ml: '26px', borderLeft: 2, borderColor: 'divider', pl: 1.75, my: 0.5 }}>
              <List component="div" disablePadding>
                <ListItemButton dense sx={{ py: 0.75, color: 'inherit' }} onClick={() => rc.uiRouter.navigate(ROUTE_TREE.WORKFLOW.LIST)} selected={isActive('/workflow/list')}>
                  <ListItemText primary="Workflows" primaryTypographyProps={{ sx: { fontWeight: 500, color: 'inherit' } }} />
                </ListItemButton>
              </List>
            </Box>
          </Collapse>
        </>
      )}
 
      {/* Data Integration Tools - Hide for migrated projects */}
      {!isMigratedProject && (
        <>
          <ListItem disablePadding>
            <ListItemButton 
              onClick={() => toggleGroup('dataIntegrationTools')} 
              selected={expandedGroups['dataIntegrationTools']} 
              sx={!isExpanded ? {
                display: 'flex',
                justifyContent: 'center',
                padding: '8px !important'
              } : { gap: 1 }}
            >
              <ListItemIcon sx={{ minWidth: isExpanded ? '30px' : 'auto' }}>
                <LucideSettings size={18} style={{ fontSize: headerIconSx.fontSize, opacity: headerIconSx.opacity }} />
              </ListItemIcon>
              {isExpanded && <ListItemText primary="Data Integration Tools" primaryTypographyProps={{ sx: headerTextSx }} />}
              {isExpanded && (
                <LucideChevronDown size={18} style={{ marginLeft: 'auto', transform: expandedGroups['dataIntegrationTools'] ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }} />
              )}
            </ListItemButton>
          </ListItem>
          <Collapse in={expandedGroups['dataIntegrationTools'] && isExpanded} timeout="auto" unmountOnExit>
            <Box sx={{ ml: '26px', borderLeft: 2, borderColor: 'divider', pl: 1.75, my: 0.5 }}>
              <List component="div" disablePadding>
                <ListItemButton dense disabled sx={{ py: 0.75, color: 'inherit', opacity: 1 }}>
                  <ListItemText primary="Format Test" primaryTypographyProps={{ sx: { color: 'inherit', opacity: 1 } }} />
                </ListItemButton>
                <ListItemButton dense disabled sx={{ py: 0.75, color: 'inherit', opacity: 1 }}>
                  <ListItemText primary="Data Sanity" primaryTypographyProps={{ sx: { color: 'inherit', opacity: 1 } }} />
                </ListItemButton>
                <ListItemButton dense disabled sx={{ py: 0.75, color: 'inherit', opacity: 1 }}>
                  <ListItemText primary="Data Validations" primaryTypographyProps={{ sx: { color: 'inherit', opacity: 1 } }} />
                </ListItemButton>
                <ListItemButton dense disabled sx={{ py: 0.75, color: 'inherit', opacity: 1 }}>
                  <ListItemText primary="Ingestion Monitor" primaryTypographyProps={{ sx: { color: 'inherit', opacity: 1 } }} />
                </ListItemButton>
              </List>
            </Box>
          </Collapse>
        </>
      )}
 
      {/* Content Management */}
      <ListItem disablePadding>
        <ListItemButton 
          onClick={() => toggleGroup('contentManagement')} 
          selected={expandedGroups['contentManagement']} 
          sx={!isExpanded ? {
            display: 'flex',
            justifyContent: 'center',
            padding: '8px !important'
          } : { gap: 1 }}
        >
          <ListItemIcon sx={{ minWidth: isExpanded ? '30px' : 'auto' }}>
            <LucidePalette size={18} style={{ fontSize: headerIconSx.fontSize, opacity: headerIconSx.opacity }} />
          </ListItemIcon>
          {isExpanded && <ListItemText primary="Content Management" primaryTypographyProps={{ sx: headerTextSx }} />}
          {isExpanded && (
            <LucideChevronDown size={18} style={{ marginLeft: 'auto', transform: expandedGroups['contentManagement'] ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }} />
          )}
        </ListItemButton>
      </ListItem>
      <Collapse in={expandedGroups['contentManagement'] && isExpanded} timeout="auto" unmountOnExit>
        <Box sx={{ ml: '26px', borderLeft: 2, borderColor: 'divider', pl: 1.75, my: 0.5 }}>
          <List component="div" disablePadding>
            {/* For migrated projects, only show CMS entities */}
            {isMigratedProject ? (
              <>
                {getCMSEntries()}
                {canAccessRoute(ROUTE_TREE.CONTENT_MANAGEMENT.MEDIA_CONTENT) && (
                  <ListItemButton onClick={navToMediaContent} selected={isActive('/content-management/media-content')} dense sx={{ py: 0.75, color: 'inherit', opacity: 1 }}>
                    <ListItemText primary="Media Content" primaryTypographyProps={{ sx: { color: 'inherit', opacity: 1 } }} />
                  </ListItemButton>
                )}
              </>
            ) : (
              <>
                {canAccessRoute(ROUTE_TREE.CONTENT_MANAGEMENT.WYSIWYG) && (
                  <ListItemButton onClick={navToCXEditor} selected={isActive('/content-management/wysiwyg')} dense sx={{ py: 0.75, color: 'inherit', opacity: 1 }}>
                    <ListItemText primary="CX Template Editor" primaryTypographyProps={{ sx: { color: 'inherit', opacity: 1 } }} />
                  </ListItemButton>
                )}
                {canAccessRoute(ROUTE_TREE.CONTENT_MANAGEMENT.DATA_SCENARIOS) && (
                  <ListItemButton onClick={navToDataScenarios} selected={isActive('/content-management/data-scenarios')} dense sx={{ py: 0.75, color: 'inherit', opacity: 1 }}>
                    <ListItemText primary="Data Scenarios" primaryTypographyProps={{ sx: { color: 'inherit', opacity: 1 } }} />
                  </ListItemButton>
                )}
                {getCMSEntries()}
                {canAccessRoute(ROUTE_TREE.CONTENT_MANAGEMENT.COLOR_PALETTE) && (
                  <ListItemButton onClick={navToColorPalette} selected={isActive('/content-management/color-palette')} dense sx={{ py: 0.75, color: 'inherit', opacity: 1 }}>
                    <ListItemText primary="Color Palette" primaryTypographyProps={{ sx: { color: 'inherit', opacity: 1 } }} />
                  </ListItemButton>
                )}
                {canAccessRoute(ROUTE_TREE.CONTENT_MANAGEMENT.MEDIA_CONTENT) && (
                  <ListItemButton onClick={navToMediaContent} selected={isActive('/content-management/media-content')} dense sx={{ py: 0.75, color: 'inherit', opacity: 1 }}>
                    <ListItemText primary="Media Content" primaryTypographyProps={{ sx: { color: 'inherit', opacity: 1 } }} />
                  </ListItemButton>
                )}
              </>
            )}
            {/* <ListItemButton dense disabled sx={{ py: 0.75, color: 'inherit', opacity: 1 }}>
              <ListItemText primary="NBI" primaryTypographyProps={{ sx: { color: 'inherit', opacity: 1 } }} />
            </ListItemButton>
            <ListItemButton dense disabled sx={{ py: 0.75, color: 'inherit', opacity: 1 }}>
              <ListItemText primary="Web Content" primaryTypographyProps={{ sx: { color: 'inherit', opacity: 1 } }} />
            </ListItemButton>
            <ListItemButton dense disabled sx={{ py: 0.75, color: 'inherit', opacity: 1 }}>
              <ListItemText primary="Email Content" primaryTypographyProps={{ sx: { color: 'inherit', opacity: 1 } }} />
            </ListItemButton>
            <ListItemButton dense disabled sx={{ py: 0.75, color: 'inherit', opacity: 1 }}>
              <ListItemText primary="HER Content" primaryTypographyProps={{ sx: { color: 'inherit', opacity: 1 } }} />
            </ListItemButton>
            <ListItemButton dense disabled sx={{ py: 0.75, color: 'inherit', opacity: 1 }}>
              <ListItemText primary="Survey" primaryTypographyProps={{ sx: { color: 'inherit', opacity: 1 } }} />
            </ListItemButton> */}
          </List>
        </Box>
      </Collapse>
 
      {/* Configuration Tools - Only Project Configs for migrated projects */}
      <ListItem disablePadding>
        <ListItemButton 
          onClick={() => toggleGroup('configurationTools')} 
          selected={expandedGroups['configurationTools']} 
          sx={!isExpanded ? {
            display: 'flex',
            justifyContent: 'center',
            padding: '8px !important'
          } : { gap: 1 }}
        >
          <ListItemIcon sx={{ minWidth: isExpanded ? '30px' : 'auto' }}>
            <LucideSliders size={18} style={{ fontSize: headerIconSx.fontSize, opacity: headerIconSx.opacity }} />
          </ListItemIcon>
          {isExpanded && <ListItemText primary="System Configuration" primaryTypographyProps={{ sx: headerTextSx }} />}
          {isExpanded && (
            <LucideChevronDown size={18} style={{ marginLeft: 'auto', transform: expandedGroups['configurationTools'] ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }} />
          )}
        </ListItemButton>
      </ListItem>
      <Collapse in={expandedGroups['configurationTools'] && isExpanded} timeout="auto" unmountOnExit>
        <Box sx={{ ml: '26px', borderLeft: 2, borderColor: 'divider', pl: 1.75, my: 0.5 }}>
          <List component="div" disablePadding>
            {/* Project Configs - shown for all projects */}
            {canAccessRoute(ROUTE_TREE.PROJECT.CONFIGS) && (
              <ListItemButton onClick={navToProjectConfigs} selected={isActive('/project/configs')} dense sx={{ py: 0.75, color: 'inherit', opacity: 1 }}>
                <ListItemText primary="Project Configs" primaryTypographyProps={{ sx: { color: 'inherit', opacity: 1 } }} />
              </ListItemButton>
            )}
            {!isMigratedProject && (
              <>
                {/* Rate Plans */}
                <ListItemButton dense disabled sx={{ py: 0.75, color: 'inherit', opacity: 1 }}>
                  <ListItemText primary="Rate Plans" primaryTypographyProps={{ sx: { color: 'inherit', opacity: 1 } }} />
                </ListItemButton>
                {/* Cluster Configuration */}
                {canAccessRoute(ROUTE_TREE.CONFIG_TOOLS.DCX_CLUSTERS) && (
                  <ListItemButton onClick={navToDCXClusters} selected={isActive('/config-tools/dcx-clusters')} dense sx={{ py: 0.75, color: 'inherit', opacity: 1 }}>
                    <ListItemText primary="Cluster Configuration" primaryTypographyProps={{ sx: { color: 'inherit', opacity: 1 } }} />
                  </ListItemButton>
                )}
                {/* WYSIWYG */}
                <ListItemButton dense disabled sx={{ py: 0.75, color: 'inherit', opacity: 1 }}>
                  <ListItemText primary="WYSIWYG" primaryTypographyProps={{ sx: { color: 'inherit', opacity: 1 } }} />
                </ListItemButton>
                {/* Config Compare */}
                {canAccessRoute(ROUTE_TREE.CONFIG_TOOLS.CONFIG_COMPARE) && (
                  <ListItemButton onClick={navToConfigCompare} selected={isActive('/config-tools/config-compare')} dense sx={{ py: 0.75, color: 'inherit', opacity: 1 }}>
                    <ListItemText primary="Config Compare" primaryTypographyProps={{ sx: { color: 'inherit', opacity: 1 } }} />
                  </ListItemButton>
                )}
              </>
            )}
          </List>
        </Box>
      </Collapse>
 
      {/* Deployment Tools - Hide for migrated projects */}
      {!isMigratedProject && (
        <>
          <ListItem disablePadding>
            <ListItemButton 
              onClick={() => toggleGroup('deploymentTools')} 
              selected={expandedGroups['deploymentTools']} 
              sx={!isExpanded ? {
                display: 'flex',
                justifyContent: 'center',
                padding: '8px !important'
              } : { gap: 1 }}
            >
              <ListItemIcon sx={{ minWidth: isExpanded ? '30px' : 'auto' }}>
                <LucideSettings size={18} style={{ fontSize: headerIconSx.fontSize, opacity: headerIconSx.opacity }} />
              </ListItemIcon>
              {isExpanded && <ListItemText primary="Deployment Tools" primaryTypographyProps={{ sx: headerTextSx }} />}
              {isExpanded && (
                <LucideChevronDown size={18} style={{ marginLeft: 'auto', transform: expandedGroups['deploymentTools'] ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }} />
              )}
            </ListItemButton>
          </ListItem>
          <Collapse in={expandedGroups['deploymentTools'] && isExpanded} timeout="auto" unmountOnExit>
            <Box sx={{ ml: '26px', borderLeft: 2, borderColor: 'divider', pl: 1.75, my: 0.5 }}>
              <List component="div" disablePadding>
                <ListItemButton dense disabled sx={{ py: 0.75, color: 'inherit', opacity: 1 }}>
                  <ListItemText primary="Crawl-Walk-Run" primaryTypographyProps={{ sx: { color: 'inherit', opacity: 1 } }} />
                </ListItemButton>
                <ListItemButton dense disabled sx={{ py: 0.75, color: 'inherit', opacity: 1 }}>
                  <ListItemText primary="Email Staging" primaryTypographyProps={{ sx: { color: 'inherit', opacity: 1 } }} />
                </ListItemButton>
              </List>
            </Box>
          </Collapse>
        </>
      )}
 
      {/* Data Analysis Tools - Hide for migrated projects */}
      {!isMigratedProject && (
        <>
          <ListItem disablePadding>
            <ListItemButton 
              onClick={() => toggleGroup('dataAnalysisTools')} 
              selected={expandedGroups['dataAnalysisTools']} 
              sx={!isExpanded ? {
                display: 'flex',
                justifyContent: 'center',
                p: 1,
              } : { gap: 1 }}
            >
              <ListItemIcon sx={{ minWidth: isExpanded ? '30px' : 'auto' }}>
                <LucideClipboardList size={18} style={{ fontSize: headerIconSx.fontSize, opacity: headerIconSx.opacity }} />
              </ListItemIcon>
              {isExpanded && <ListItemText primary="Data Analysis Tools" primaryTypographyProps={{ sx: headerTextSx }} />}
              {isExpanded && (
                <LucideChevronDown size={18} style={{ marginLeft: 'auto', transform: expandedGroups['dataAnalysisTools'] ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }} />
              )}
            </ListItemButton>
          </ListItem>
          <Collapse in={expandedGroups['dataAnalysisTools'] && isExpanded} timeout="auto" unmountOnExit>
            <Box sx={{ ml: '26px', borderLeft: 2, borderColor: 'divider', pl: 1.75, my: 0.5 }}>
              <List component="div" disablePadding>
                <ListItemButton dense disabled sx={{ py: 0.75, color: 'inherit', opacity: 1 }}>
                  <ListItemText primary="Project Readiness Score" primaryTypographyProps={{ sx: { color: 'inherit', opacity: 1 } }} />
                </ListItemButton>
                <ListItemButton dense disabled sx={{ py: 0.75, color: 'inherit', opacity: 1 }}>
                  <ListItemText primary="Email Campaign Analytics" primaryTypographyProps={{ sx: { color: 'inherit', opacity: 1 } }} />
                </ListItemButton>
              </List>
            </Box>
          </Collapse>
        </>
      )}
 
    </React.Fragment>
    )  
  )
}